import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Star } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { SUPPORT_PHONE, SUPPORT_TEL_HREF } from '@/lib/constants';

/**
 * /review/:token — PUBLIC. The link in the "how did we do?" email.
 *
 * Opening it records nothing (mail scanners open links too); the rating is
 * saved only when the person presses Submit. `?r=4` pre-selects a star.
 * After submitting, EVERYONE sees the Google / Yelp / Facebook buttons —
 * the public review sites forbid asking only happy customers.
 * `?optout=1` offers to stop these emails.
 */

interface Info {
  customer_name: string | null;
  location_name: string | null;
  job_number: string | null;
  rating: number | null;
  comment: string | null;
  links: { google: string; yelp: string; facebook: string };
}

const LABELS = ['', 'Poor', 'Not great', 'OK', 'Good', 'Excellent'];

export default function Review() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const preset = Number(params.get('r'));
  const optOutAsked = params.get('optout') === '1';

  const [info, setInfo] = useState<Info | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [rating, setRating] = useState<number>(Number.isInteger(preset) && preset >= 1 && preset <= 5 ? preset : 0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [optedOut, setOptedOut] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`/.netlify/functions/review-response?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!alive) return;
        if (!r.ok || !data.ok) {
          setLoadErr(data.error ?? 'This link is not valid any more.');
          return;
        }
        setInfo(data);
        if (data.rating && !rating) setRating(data.rating);
        if (data.comment) setComment(data.comment);
      })
      .catch(() => alive && setLoadErr('Could not load this page. Please try again.'));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function post(body: Record<string, unknown>) {
    setBusy(true);
    setErr(null);
    try {
      const r = await fetch('/.netlify/functions/review-response', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token, ...body }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) throw new Error(data.error ?? 'Something went wrong. Please try again.');
      return data;
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!rating) {
      setErr('Pick 1 to 5 stars.');
      return;
    }
    if (await post({ rating, comment })) setDone(true);
  }

  const where = info
    ? info.location_name && info.location_name !== info.customer_name
      ? info.location_name
      : info.customer_name
    : null;
  const shown = hover || rating;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <img src="/brix-logo-full.png" alt="Brix Beverage" className="mb-6 h-10" />

        {loadErr ? (
          <p className="text-slate-700">{loadErr}</p>
        ) : !info ? (
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" aria-label="Loading" />
        ) : optedOut ? (
          <p className="text-slate-700">Done — we won't ask you to rate visits again.</p>
        ) : done ? (
          <div>
            <div className="mb-3 flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-6 w-6" />
              <h1 className="text-xl font-semibold">Thank you!</h1>
            </div>
            <p className="mb-5 text-slate-700">
              {rating <= 3
                ? 'Our service team will be in touch. If it can’t wait, call us at '
                : 'We really appreciate it. '}
              {rating <= 3 && (
                <a href={SUPPORT_TEL_HREF} className="font-medium text-brand underline">
                  {SUPPORT_PHONE}
                </a>
              )}
            </p>
            <p className="mb-3 text-sm text-slate-600">Would you share a review publicly too? It helps other businesses find us.</p>
            <div className="flex flex-wrap gap-2">
              <ReviewLink href={info.links.google} label="Review on Google" />
              <ReviewLink href={info.links.yelp} label="Review on Yelp" />
              <ReviewLink href={info.links.facebook} label="Review on Facebook" />
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h1 className="mb-2 text-2xl font-semibold text-brand">How did we do?</h1>
            <p className="mb-6 text-slate-700">
              Rate our service visit{where ? ` at ${where}` : ''}
              {info.job_number ? ` (job #${info.job_number})` : ''}.
            </p>

            <div className="mb-2 flex gap-1" role="radiogroup" aria-label="Rating" onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={rating === n}
                  aria-label={`${n} star${n > 1 ? 's' : ''}`}
                  onMouseEnter={() => setHover(n)}
                  onClick={() => setRating(n)}
                  className="rounded p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                  <Star className={cn('h-10 w-10', n <= shown ? 'fill-amber-400 text-amber-400' : 'text-slate-300')} />
                </button>
              ))}
            </div>
            <p className="mb-5 h-5 text-sm text-slate-600">{LABELS[shown] ?? ''}</p>

            <label htmlFor="review-comment" className="mb-1 block text-sm font-medium text-slate-700">
              Anything to add? <span className="font-normal text-slate-500">(optional)</span>
            </label>
            <textarea
              id="review-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              maxLength={2000}
              className="mb-4 w-full rounded-lg border border-slate-300 p-3 text-sm focus:border-brand focus:outline-none"
            />

            {err && <p className="mb-3 text-sm text-red-700">{err}</p>}
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : info.rating ? 'Update my rating' : 'Submit'}
            </Button>

            {optOutAsked && (
              <button
                type="button"
                disabled={busy}
                onClick={async () => {
                  if (await post({ opt_out: true })) setOptedOut(true);
                }}
                className="mt-4 w-full text-center text-sm text-slate-500 underline"
              >
                Don't ask me to rate visits again
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

function ReviewLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
    >
      {label}
    </a>
  );
}
